import { format } from 'date-fns'
import type { AllNegotiationVariables } from '@/features/negotiations/types/block-editor-variables.types'
import { formatDate } from '../templates/utils/format'

/**
 * Datos de prueba realistas para el sistema de variables
 * Útil para previsualización y testing de templates
 */
export const MOCK_NEGOTIATION_VARIABLES: AllNegotiationVariables = {
  // 📝 Datos de la Sociedad
  nombre_sociedad: 'Desarrollos Inmobiliarios San José S.A.',

  // 🏢 Datos del Proyecto
  nombre_proyecto: 'Residencial Vista Hermosa',
  codigo_proyecto: 'VH-2024-001',

  // 🏠 Datos de la Propiedad
  nombre_propiedad: 'Casa Modelo Premium',
  codigo_propiedad: 'VHCASA205',
  tipo_propiedad: 'VH205',
  tipo_nombre_propiedad: 'Casa',
  upgrades: 'SI',
  parqueo_sotano: '2 parqueos techados + sótano de almacenamiento',
  forma_pago_inmueble: 'Crédito Bancario',
  terreno_m2: '180',
  terreno_m2_letras: 'Ciento Ochenta Punto Cero',
  terreno_v2: '210',
  terreno_v2_letras: 'Doscientos Diez Punto Cero',
  terreno_extra: '25',
  terreno_extra_letras: 'Veinticinco Punto Cero',
  construccion_m2: '150',
  construccion_m2_letras: 'Ciento Cincuenta Punto Cero',
  parqueo_m2: '10',
  parqueo_m2_letras: 'Diez Punto Cero',
  // Datos adicionales desde propiedad
  finca: 'Finca 123',
  folio: '1234',
  libro: '4321',
  monto_iusi: '100000',
  contador_agua: '12345678',
  contador_electricidad: 'T80865',
  telefono_propiedad: '2222-2222',
  valor_total: '850000.00',
  valor_total_con_moneda: 'Q 850,000.00',
  reserva: '50000',
  total_cuotas: '150000',
  monto_financiar: '650000',
  fecha_primera_cuota: '2026-02-11',
  fecha_ultima_cuota: '2028-07-26',
  fecha_inicio_plazo: '2026-08-11',
  moneda_simbolo: 'Q',
  moneda_singular: 'QUETZAL',
  moneda_plural: 'QUETZALES',
  centavo_singular: 'Centavo de Quetzal',
  centavo_plural: 'Centavos de Quetzal',
  fecha_entrega: '15/01/2027',

  // 🏠 Identificación del inmueble (desde propiedad)
  unidad: 'Apartamento',
  unidad_tipo: '3',
  unidad_tipo_letras: 'Tres',
  estructura: 'Torre',
  estructura_numero: '1',
  estructura_numero_letras: 'Uno',
  estructura_codigo: 'T1',
  identificador: '303',
  identificador_letras: 'Trescientos Tres',

  // 🅿️ Parqueo (acuerdo de pago)
  tiene_parqueo: 'SI',
  parqueo_numero: '168',
  parqueo_numero_letras: 'Ciento Sesenta y Ocho',
  parqueo_m2_negociacion: '25.00',
  parqueo_m2_negociacion_letras: 'Veinticinco Punto Cero',
  parqueo_vehiculos: '2',
  parqueo_vehiculos_letras: 'Dos',
  parqueo_nivel: '2',
  parqueo_nivel_letras: 'Dos',

  // 🏚️ Bodega (acuerdo de pago) — nombres canónicos
  tiene_bodega: 'SI',
  bodega_numero: '4',
  bodega_numero_letras: 'Cuatro',
  bodega_m2: '2.56',
  bodega_m2_letras: 'Dos Punto Cincuenta y Seis',
  bodega_sotano: '1',
  bodega_sotano_letras: 'Uno',

  // 💰 Valores sin impuestos (acuerdo de pago)
  valor_titulo_sin_impuesto: '780000',
  valor_titulo_sin_impuesto_letras: 'Setecientos Ochenta Mil Punto Cero',
  valor_total_sin_impuesto: '850000',
  valor_total_sin_impuesto_letras: 'Ochocientos Cincuenta Mil Punto Cero',
  valor_upgrades_sin_impuesto: '25000',
  valor_upgrades_sin_impuesto_letras: 'Veinticinco Mil Punto Cero',
  valor_inmueble_sin_impuesto: '825000',
  valor_inmueble_sin_impuesto_letras: 'Ochocientos Veinticinco Mil Punto Cero',

  // Legacy — compatibilidad con plantillas anteriores
  tiene_sotano: 'SI',
  sotano_numero: '2',
  sotano_numero_letras: 'Dos',
  sotano_m2: '2.56',
  sotano_m2_letras: 'Dos Punto Cincuenta y Seis',
  sotano_nivel: '1',
  sotano_nivel_letras: 'Uno',

  // 👥 Datos del Propietario
  nombre_propietario: 'María Elena Rodríguez López',
  nit_propietario: '12345678-9',
  email_propietario: 'jperez@email.com',
  telefono_propietario: '5551-2345',
  nombre_representante_legal: 'Juan Carlos Pérez García',
  nit_representante_legal: '98765432-1',
  email_representante_legal: 'juan.perez@gmail.com',
  telefono_representante_legal: '5559-8765',
  nombre_propietario_representante_legal: 'María Elena Rodríguez López',

  // 📄 Datos del Documento
  fecha_impresion: formatDate(new Date().toISOString()),

  // 👨‍💼 Datos del Asesor
  nombre_asesor: 'Lic. Roberto Méndez Castillo',

  // 🔄 Otros Datos
  fecha_de_hoy: formatDate(new Date().toISOString()),
  hora_actual: format(new Date(), 'HH:mm a')
}

/**
 * Variantes de datos de prueba para diferentes escenarios
 */
export const MOCK_SCENARIOS = {
  // Escenario básico - Cliente individual
  individual_client: {
    ...MOCK_NEGOTIATION_VARIABLES,
    nombre_propietario: 'Ana Sofía González Morales',
    nit_propietario: '87654321-0',
    email_propietario: 'agonzalez@email.com',
    telefono_propietario: '5555-1234',
    nombre_proyecto: 'Condominio Los Cipreses',
    codigo_proyecto: 'LC-2024-002',
    valor_total: '650000',
    valor_total_con_moneda: 'Q650,000.00'
  },

  // Escenario empresa - Cliente jurídico
  juridical_client: {
    ...MOCK_NEGOTIATION_VARIABLES,
    nombre_propietario: 'Inversiones Guatemaltecas, S.A.',
    nit_propietario: '45678912-3',
    email_propietario: 'info@inversionesgt.com',
    telefono_propietario: '2334-5678',
    nombre_proyecto: 'Torres Empresariales Zona 10',
    codigo_proyecto: 'TEZ10-2024-001',
    tipo_propiedad: 'Oficina ejecutiva 250m²',
    valor_total: '1250000',
    valor_total_con_moneda: 'Q1,250,000.00'
  },

  // Escenario premium - Propiedades de lujo
  premium_property: {
    ...MOCK_NEGOTIATION_VARIABLES,
    nombre_propietario: 'Dr. Carlos Eduardo Ramírez Herrera',
    nit_propietario: '11223344-5',
    nombre_proyecto: 'Residencial Exclusivo La Cañada',
    codigo_proyecto: 'RLC-2024-PREMIUM',
    tipo_propiedad: 'Villa con piscina y jardín privado',
    terreno_m2: '350',
    terreno_v2: '420',
    valor_total: '1850000',
    valor_total_con_moneda: 'Q1,850,000.00',
    upgrades: 'SÍ - Cocina italiana, pisos de mármol, sistema domótico',
    parqueo_sotano: '4 parqueos cubiertos + garaje para 2 vehículos adicionales'
  }
}

/**
 * Función para obtener datos de prueba según el escenario
 */
export const getMockVariables = (
  scenario: keyof typeof MOCK_SCENARIOS = 'individual_client'
): AllNegotiationVariables => {
  return MOCK_SCENARIOS[scenario] || MOCK_NEGOTIATION_VARIABLES
}

/**
 * Función para generar variables aleatorias para testing
 */
export const generateRandomMockVariables = (): AllNegotiationVariables => {
  const scenarios = Object.keys(MOCK_SCENARIOS) as Array<keyof typeof MOCK_SCENARIOS>
  const randomScenario = scenarios[Math.floor(Math.random() * scenarios.length)]
  return getMockVariables(randomScenario)
}
